import { Paper, Typography, Box, Alert } from "@mui/material";
import { AsyncAutoCompleteNew } from "../../../../components/async-auto-complete/AsyncAutoCompleteNew";
import { treatmentAutoCompleteLabel } from "../../../../utils/treatmentAutoCompleteLabel";
import { UseFormReturn } from "react-hook-form";
import { formatDate } from "../../../../utils/dateUtils";
import { useEffect } from "react";
import { FieldFromParam } from "../../../../api/useItemsFromSearchParams";

type EEGSelectionProps = {
    entityForm: UseFormReturn;
    selectedTreatment: any;
    hasSameEvaluationSelected: boolean;
    fieldsFromParams: Array<FieldFromParam>;
};

export const EEGSelection = ({
    entityForm,
    selectedTreatment,
    hasSameEvaluationSelected,
    fieldsFromParams,
}: EEGSelectionProps) => {
    useEffect(() => {
        // Reset EEG selections when treatment changes
        entityForm.setValue("firstEEG", null);
        entityForm.setValue("secondEEG", null);
    }, [selectedTreatment, entityForm]);

    return (
        <Paper sx={{ p: 3, mb: 4 }}>
            <Typography variant="h5" color="primary" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                Seleccione o tratamento as avaliações para comparar
            </Typography>

            <Box sx={{ mb: 3 }}>
                <AsyncAutoCompleteNew
                    entityForm={entityForm}
                    fieldName="treatment"
                    recordsOptions={{
                        entity: "treatments",
                        fieldsToQuery:
                            "*,client.user_id.first_name,client.user_id.last_name, client.client_id,theurapists_involved_in_treamtent.technicians_id.*.*,client_owner.*.*",
                        customFilter: (value: string) => ({
                            client: {
                                client_id: {
                                    _icontains: value,
                                },
                            },
                        }),
                        labelKey: (option) => treatmentAutoCompleteLabel(option),
                        idFromParams: fieldsFromParams?.find((field) => field.entity === "treatments")?.value,
                    }}
                />
            </Box>

            {selectedTreatment && (
                <Box sx={{ display: "flex", gap: 2 }}>
                    <AsyncAutoCompleteNew
                        entityForm={entityForm}
                        fieldName="firstEEG"
                        enabled={true}
                        placeholder="Primeiro EEG"
                        recordsOptions={{
                            entity: "eeg_recording",
                            fieldsToQuery: "*.*, eeg_evaluation.eeg_session.start_date",
                            filter: {
                                eeg_evaluation: {
                                    treatment: {
                                        _eq: selectedTreatment.id,
                                    },
                                },
                            },
                            sort: "eeg_evaluation.eeg_session.start_date",
                            labelKey: formatEEGLabel,
                        }}
                    />
                    <AsyncAutoCompleteNew
                        entityForm={entityForm}
                        fieldName="secondEEG"
                        enabled={true}
                        placeholder="Segundo EEG"
                        recordsOptions={{
                            entity: "eeg_recording",
                            fieldsToQuery: "*.*, eeg_evaluation.eeg_session.start_date",
                            filter: {
                                eeg_evaluation: {
                                    treatment: {
                                        _eq: selectedTreatment.id,
                                    },
                                },
                            },
                            sort: "eeg_evaluation.eeg_session.start_date",
                            labelKey: formatEEGLabel,
                        }}
                    />
                </Box>
            )}

            {hasSameEvaluationSelected && (
                <Alert severity="error" sx={{ mt: 2 }}>
                    Por favor, selecione avaliações diferentes para comparação pré-tratamento e pós-tratamento.
                </Alert>
            )}
        </Paper>
    );
};

export const formatTextWithSplitAndCapitalize = (text: string, splitChar: string = "_") => {
    const words = text.split(splitChar);
    const capitalizedWords = words.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
    return capitalizedWords.join(" ");
};

// Utility function to format EEG label
export const formatEEGLabel = (option: any) => {
    const formattedDate = formatDate(option?.eeg_evaluation?.eeg_session?.start_date) || "No date";
    const formattedCondition = formatTextWithSplitAndCapitalize(option?.eyes_condition);
    return `${formattedDate} - ${formattedCondition}`;
};
